import React from 'react';
import Icon from '../../../components/AppIcon';

const DownloadTrendsChart = ({ trends, period }) => {
  if (!trends || trends?.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 text-center">
        <Icon name="TrendingUp" size={48} className="mx-auto mb-3 text-muted-foreground" />
        <p className="text-muted-foreground">No download data available</p>
      </div>
    );
  }

  const maxDownloads = Math.max(...trends?.map(t => t?.downloads), 1);
  const chartHeight = 200;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Calculate points for the line chart
  const points = trends?.map((item, index) => {
    const x = (index / (trends?.length - 1)) * 100;
    const y = chartHeight - (item?.downloads / maxDownloads) * chartHeight;
    return { x, y, downloads: item?.downloads, date: item?.date };
  });

  // Create SVG path
  const pathData = points?.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point?.x} ${point?.y}`)?.join(' ');

  // Create area fill path
  const areaPathData = `${pathData} L 100 ${chartHeight} L 0 ${chartHeight} Z`;

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base md:text-lg font-semibold mb-1">Download Trends</h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Last {period} days
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-lg">
          <Icon name="Download" size={16} className="text-primary" />
          <span className="text-sm font-medium data-text">
            {trends?.reduce((sum, t) => sum + t?.downloads, 0)} total
          </span>
        </div>
      </div>
      <div className="relative" style={{ height: `${chartHeight}px` }}>
        <svg
          viewBox={`0 0 100 ${chartHeight}`}
          preserveAspectRatio="none"
          className="w-full h-full"
        >
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1]?.map((ratio, index) => (
            <line
              key={index}
              x1="0"
              y1={chartHeight * ratio}
              x2="100"
              y2={chartHeight * ratio}
              stroke="var(--color-border)"
              strokeWidth="0.5"
              strokeDasharray="2,2"
            />
          ))}

          {/* Area fill */}
          <path
            d={areaPathData}
            fill="var(--color-primary)"
            fillOpacity="0.1"
          />

          {/* Line */}
          <path
            d={pathData}
            fill="none"
            stroke="var(--color-primary)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points */}
          {points?.map((point, index) => (
            <g key={index}>
              <circle
                cx={point?.x}
                cy={point?.y}
                r="3"
                fill="var(--color-primary)"
                className="hover:r-4 transition-all cursor-pointer"
              />
              <title>{`${formatDate(point?.date)}: ${point?.downloads} downloads`}</title>
            </g>
          ))}
        </svg>
      </div>
      {/* X-axis labels */}
      <div className="flex justify-between mt-3 text-xs text-muted-foreground">
        <span>{formatDate(trends?.[0]?.date)}</span>
        {trends?.length > 2 && (
          <span>{formatDate(trends?.[Math.floor(trends?.length / 2)]?.date)}</span>
        )}
        <span>{formatDate(trends?.[trends?.length - 1]?.date)}</span>
      </div>
      {/* Y-axis labels */}
      <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-muted-foreground pr-2">
        <span className="data-text">{maxDownloads}</span>
        <span className="data-text">{Math.floor(maxDownloads * 0.5)}</span>
        <span className="data-text">0</span>
      </div>
    </div>
  );
};

export default DownloadTrendsChart;